import { useContext, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { GoFlyerAdminContext } from "../contextApi/context";
import { fetchAnalyticsDashboard } from "../utils/analytics/adminAnalyticsApi";
import { AnalyticsApiError } from "../utils/analytics/adminAnalyticsApi";
import {
  AnalyticsDashboardResults,
  AnalyticsFilters,
} from "../utils/analytics/types";
import { resolveAuthToken } from "./useAuthHeaders";
import { getAuthToken } from "../utils/authTokenStorage";
import { analyticsKeys } from "../app/pages/AnalyticsPage/queryKeys";
import { routes } from "../utils/constant/navigationRoutes";

function resolveDashboardAuthToken(user?: {
  jwtToken?: string;
  token?: string;
  isLoggedIn?: boolean;
}): string | undefined {
  return resolveAuthToken(user) ?? getAuthToken() ?? undefined;
}

export function useAnalyticsDashboard(filters: AnalyticsFilters) {
  const navigate = useNavigate();
  const { state } = useContext(GoFlyerAdminContext);
  const authToken = resolveDashboardAuthToken(state.user);
  const canFetch = Boolean(state.user?.isLoggedIn && authToken);

  const query = useQuery<AnalyticsDashboardResults, AnalyticsApiError>({
    queryKey: analyticsKeys.dashboard(filters),
    queryFn: () => {
      const token = resolveDashboardAuthToken(state.user);
      if (!token) {
        throw new AnalyticsApiError(401, "No auth token");
      }

      return fetchAnalyticsDashboard(filters, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    },
    enabled: canFetch,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.error?.status === 401) {
      navigate(routes.core.login, { replace: true });
    }
  }, [query.error, navigate]);

  const isUnavailable = query.error?.status === 503;
  const isScopeViolation = query.error?.status === 403;

  return {
    ...query,
    isUnavailable,
    isScopeViolation,
  };
}
